import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BrutalistCard, BrutalistBadge, BrutalistButton } from './BrutalistUI';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const FlashcardView = ({ data }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [direction, setDirection] = useState(0);

  const term = data[currentIndex];

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.code === 'Space') {
        e.preventDefault();
        setIsFlipped(prev => !prev);
      } else if (e.code === 'ArrowRight') {
        handleNext();
      } else if (e.code === 'ArrowLeft') {
        handlePrev();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex]);

  const handleNext = () => {
    if (currentIndex < data.length - 1) {
      setDirection(1);
      setIsFlipped(false);
      setCurrentIndex(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      setDirection(-1);
      setIsFlipped(false);
      setCurrentIndex(prev => prev - 1);
    }
  };

  if (!term) return null;

  return (
    <div className="max-w-4xl mx-auto mt-10 px-4">
      <div className="flex justify-between items-end mb-6">
        <div>
          <h2 className="text-3xl font-black uppercase tracking-tighter italic">Study Mode</h2>
          <p className="font-mono text-sm font-bold opacity-60">Press SPACE to flip • Arrows to navigate</p>
        </div>
        <div className="text-right">
          <div className="text-2xl font-black bg-black text-white px-3 py-1 inline-block border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
            {currentIndex + 1} / {data.length}
          </div>
        </div>
      </div>

      <div className="relative h-[450px] w-full perspective-1000">
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={currentIndex}
            custom={direction}
            initial={{ x: direction * 100, opacity: 0, rotateY: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -direction * 100, opacity: 0 }}
            transition={{ type: 'spring', damping: 20, stiffness: 100 }}
            className="w-full h-full cursor-pointer touch-none"
            onClick={() => setIsFlipped(!isFlipped)}
          >
            <motion.div
              animate={{ rotateY: isFlipped ? 180 : 0 }}
              transition={{ duration: 0.6, type: 'spring', stiffness: 260, damping: 20 }}
              className="w-full h-full relative preserve-3d"
            >
              {/* FRONT SIDE */}
              <div className="absolute inset-0 backface-hidden">
                <BrutalistCard 
                  color={term.color} 
                  className="w-full h-full flex flex-col items-center justify-center text-center !p-12 border-8 shadow-[16px_16px_0px_0px_rgba(0,0,0,1)]"
                >
                  <BrutalistBadge className="mb-6 !text-lg !px-4">{term.category}</BrutalistBadge>
                  <h3 className="text-5xl sm:text-7xl font-black uppercase tracking-tighter leading-none break-words w-full">
                    {term.term}
                  </h3>
                  <div className="mt-12 opacity-30 animate-bounce">
                    <SafeIcon icon={FiIcons.FiRotateCw} className="text-4xl" />
                  </div>
                </BrutalistCard>
              </div>

              {/* BACK SIDE */}
              <div className="absolute inset-0 backface-hidden rotate-y-180">
                <BrutalistCard 
                  color="white" 
                  className="w-full h-full flex flex-col items-start justify-center !p-10 border-8 shadow-[16px_16px_0px_0px_rgba(0,0,0,1)]"
                >
                  <div className="space-y-6 w-full">
                    <div>
                      <span className="text-xs font-black uppercase bg-black text-white px-2 py-1 mb-2 inline-block">Definition</span>
                      <p className="text-xl sm:text-2xl font-bold leading-tight">{term.plainEnglish}</p>
                    </div>
                    
                    {term.simpleWay && (
                      <div className="bg-yellow-200 p-4 border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
                        <span className="text-xs font-black uppercase text-black block mb-1">Simple Way</span>
                        <p className="text-lg font-black italic">"{term.simpleWay}"</p>
                      </div>
                    )}

                    {term.exampleSentence && (
                      <div className="border-l-4 border-black pl-4">
                        <span className="text-xs font-black uppercase text-gray-500 block mb-1">Example</span>
                        <p className="text-base font-medium font-mono">{term.exampleSentence}</p>
                      </div>
                    )}
                  </div>
                </BrutalistCard>
              </div>
            </motion.div>
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="mt-12 flex justify-center gap-6">
        <BrutalistButton 
          onClick={handlePrev} 
          disabled={currentIndex === 0}
          className={`!p-4 ${currentIndex === 0 ? 'opacity-30' : ''}`}
        >
          <SafeIcon icon={FiIcons.FiArrowLeft} className="text-2xl" />
        </BrutalistButton>
        <BrutalistButton 
          onClick={() => setIsFlipped(!isFlipped)} 
          className="!px-10 !bg-yellow-400"
        >
          FLIP CARD
        </BrutalistButton>
        <BrutalistButton 
          onClick={handleNext} 
          disabled={currentIndex === data.length - 1}
          className={`!p-4 ${currentIndex === data.length - 1 ? 'opacity-30' : ''}`}
        >
          <SafeIcon icon={FiIcons.FiArrowRight} className="text-2xl" />
        </BrutalistButton>
      </div>

      <div className="mt-8 h-4 w-full bg-white border-4 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
        <motion.div 
          initial={{ width: 0 }}
          animate={{ width: `${((currentIndex + 1) / data.length) * 100}%` }}
          className="h-full bg-green-400 border-r-4 border-black"
        />
      </div>
    </div>
  );
};

export default FlashcardView;